import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import i18n from 'i18n-js';
import moment from 'moment';

import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import { getBoxShadowStyle } from '@styles/shadow';

import Image from '@components/common/Image';
import TouchableElement from '@components/common/touchableElement';
import ItemHistoryTime from '@components/common/ItemHistoryTime';
import { isPhone, ImageUpload } from '@helpers';
import { KEYS_TAGS } from '@components/imageHandling/common';
import { useRoute } from '@react-navigation/native';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { NCFileDetailsScreenName } from '@v2/pages/nc-file/details/navigation/config';
import { NCFileDetailsScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { Perspective } from '@v2/pages/common/enums';
import { IMAGE_UPLOAD_HEADER_TEXT, SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';

const Item = ({
  C,
  item,
  isFirst,
  isLast,
  generateImageURL,
  maxValueProductive,
  maxValueActive,
  selectedChartDate,
  setSelectedNCFile,
  machineId,
}) => {
  const [imageLink, setImageLink] = useState(null);
  const { imageData, imageHash, ncFileName } = item;
  const [loading, setLoading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);

  const possibleAddImage = item.ncFileId && !imageHash && !uploadedImage;

  const { timePeriod } = useHandleParams();
  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();

  useEffect(() => {
    (async () => {
      if (imageHash) {
        const imageLink = await generateImageURL({
          size: '_1x',
          cropped: imageData?.isCropped,
          item: { imageHash: imageHash, extensionDerivative: imageData?.extensionDerivative },
          page: imageData?.pageNumber,
        });
        setImageLink(imageLink);
      }
    })();
  }, []);

  const uploadImageFunction = () => {
    if (loading || !possibleAddImage) {
      return;
    }

    ImageUpload({
      headerText: IMAGE_UPLOAD_HEADER_TEXT.AddToNcFile,
      headerDescription: item.ncFileName,
      ncFile: item.ncFileName,
      uploadStart: () => setLoading(true),
      uploadEnd: (img) => {
        setLoading(false);
        setUploadedImage(img);
      },
      tags: [
        {
          scopeId: KEYS_TAGS['Clamping'],
          scopeReferenceId: item.ncFileId,
          isFavorite: true,
        },
      ],
    });
  };

  return (
    <TouchableElement
      testID={`${TEST_IDS.performanceNcFiles.item}-${item.ncFileId}`}
      hitSlop={15}
      onPress={() => {
        setSelectedNCFile({
          machineStatus: {
            ...item,
            ncFile: item.ncFileName,
          },
        });

        navigateToMainDetailsScreen({
          mainScreen: NCFileDetailsScreenName,
          subScreen: NCFileDetailsScreen.NC_FILE,
          params: {
            perspective: Perspective.NC_FILE,
            ncFileId: item.ncFileId,
            ncFileName: item.ncFileName,
            ncFileStatus: item.ncFileStatus,
          },
        });
      }}
    >
      {({ boxStyle, pressHoverColor }) => {
        return (
          <View
            style={[
              boxStyle,
              {
                paddingLeft: scale(18),
                backgroundColor: C(pressHoverColor('Background/Dark/Field')),
              },
              isFirst && { borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS), borderTopRightRadius: scale(SCREEN_BORDER_RADIUS) },
              isLast && { borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS), borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS) },
            ]}
          >
            <View
              style={[
                {
                  height: scale(80),
                  flexDirection: 'row',
                  alignItems: 'center',
                },
              ]}
            >
              <View
                style={{
                  width: scale(90),
                  height: scale(60),
                  justifyContent: 'center',
                  alignContent: 'center',
                  alignItems: 'center',
                }}
              >
                {possibleAddImage ? (
                  <TouchableElement onPress={uploadImageFunction}>
                    {({ boxStyle, hovered }) => (
                      <View
                        style={[
                          boxStyle,
                          {
                            backgroundColor: hovered ? C('Button/Light/Secondary hover') : 'transparent',
                            borderRadius: scale(4),
                            padding: scale(8),
                          },
                        ]}
                      >
                        <Svg icon="addPicture" color={'Button/Light/Secondary Text'} width={scale(45)} height={scale(34)} key={'image-icon'} />
                      </View>
                    )}
                  </TouchableElement>
                ) : (
                  <Image
                    {...{
                      shadowColor: 'transparent',
                      isShadow: Boolean(imageLink),
                      isOverlay: true,
                      resizeMode: 'contain',
                      style: {
                        width: scale(90),
                        height: scale(60),
                        resizeMode: 'contain',
                      },
                      source: uploadedImage ? uploadedImage : { uri: imageLink },
                      alt: 'img',
                      C,
                    }}
                  />
                )}
              </View>
              <View
                style={{
                  height: '100%',
                  paddingVertical: scale(7),
                  marginLeft: scale(18),
                  flex: 1,
                  borderBottomWidth: isLast ? 0 : StyleSheet.hairlineWidth,
                  borderColor: C('Hairline/Dark/No trans'),
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <View style={{ flex: 1 }}>
                  <Text style={[font['Button 1'], { color: C('Label/Dark/Primary') }]} numberOfLines={1}>
                    {ncFileName}
                  </Text>
                  <View>
                    <ItemHistoryTime
                      {...{
                        C,
                        paddingLeft: 0,
                        maxWidth: isPhone ? 70 : 80,
                        item: { machineStatistics: { ...item, totalLoaded: true }, maxValueProductive, maxValueActive },
                      }}
                    />
                  </View>
                </View>
                <View style={{ paddingRight: scale(16) }}>
                  <View style={[boxStyle]}>
                    <Svg icon="chevronRightLight" color="Special/Dark/Chevron" width={scale(7)} height={scale(12)} />
                  </View>
                </View>
              </View>
            </View>
          </View>
        );
      }}
    </TouchableElement>
  );
};

export default ({ C, ncFileStatistics, generateImageURL, allSelected, selectedChartDate, setSelectedNCFile, machineId }) => {
  const route = useRoute();

  return (
    <View style={{ marginTop: scale(28) }}>
      {allSelected && isPhone && (
        <View style={{ paddingHorizontal: scale(15), marginBottom: scale(5) }}>
          <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>{i18n.t(`MOST ${String(route.name).toUpperCase()} NC FILES`)}</Text>
        </View>
      )}
      <View
        testID={TEST_IDS.performanceNcFiles.container}
        style={[getBoxShadowStyle({ type: 'Field', C }), { borderRadius: scale(SCREEN_BORDER_RADIUS) }]}
      >
        {ncFileStatistics.data.map((item, index) => {
          const isFirst = index === 0;
          const isLast = index === ncFileStatistics.data.length - 1;
          return (
            <Item
              key={item.ncFileId}
              {...{
                C,
                item,
                index,
                generateImageURL,
                isFirst,
                isLast,
                maxValueActive: ncFileStatistics.maxValueActive,
                maxValueProductive: ncFileStatistics.maxValueProductive,
                machineId,
                selectedChartDate,
                setSelectedNCFile,
              }}
            />
          );
        })}
      </View>
    </View>
  );
};
